import React, { useEffect, useState } from "react";

import { getDvSourceImageUrl } from "../../../functions/themeAssets";

const QUESTION_MAX = 350;
const OPTION_MAX = 100;
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;
const OPTION_KEYS = [
  "option_one",
  "option_two",
  "option_three",
  "option_four",
  "option_five",
  "option_six",
];
const OPTION_LABELS = ["Option one", "Option two", "Option three", "Option four", "Option five", "Option six"];
const IMAGE_EXT = ["jpg", "jpeg", "png"];
const IMAGE_MAX_MB = 5;
const IMAGE_REQUIRED_SIZE = 534;

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight, url });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read image dimensions."));
    };
    img.src = url;
  });
}

function emptyDraft() {
  const d = { question_name: "", showOption: MIN_OPTIONS, correct_answer: "" };
  OPTION_KEYS.forEach((k) => (d[k] = ""));
  return d;
}

export default function QuizQuestionModal({ open, mode, initial, onClose, onSubmit, isSaving }) {
  const [draft, setDraft] = useState(emptyDraft);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [removeHint, setRemoveHint] = useState(false);
  const [localError, setLocalError] = useState("");

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    if (isEdit && initial) {
      const showOption = Math.max(MIN_OPTIONS, Math.min(MAX_OPTIONS, Number(initial.showOption) || MIN_OPTIONS));
      setDraft({
        question_name: initial.question_name || "",
        showOption,
        correct_answer: initial.correct_answer || "",
        option_one: initial.option_one || "",
        option_two: initial.option_two || "",
        option_three: initial.option_three || "",
        option_four: initial.option_four || "",
        option_five: initial.option_five || "",
        option_six: initial.option_six || "",
      });
      setCorrectIndex(Number(initial.answer_id) || null);
    } else {
      setDraft(emptyDraft());
      setCorrectIndex(null);
    }
    setFile(null);
    setFilePreview("");
    setRemoveHint(false);
    setLocalError("");
  }, [open, isEdit, initial]);

  if (!open) return null;

  const setOptionCount = (n) => {
    const count = Math.max(MIN_OPTIONS, Math.min(MAX_OPTIONS, Number(n) || MIN_OPTIONS));
    setDraft((prev) => {
      const next = { ...prev, showOption: count };
      OPTION_KEYS.forEach((key, i) => {
        if (i >= count) next[key] = "";
      });
      return next;
    });
    if (correctIndex != null && correctIndex > count) setCorrectIndex(null);
  };

  const setOption = (index, value) => {
    const key = OPTION_KEYS[index];
    setDraft((prev) => ({ ...prev, [key]: value.slice(0, OPTION_MAX) }));
    setLocalError("");
  };

  const selectCorrect = (index) => {
    setCorrectIndex(index + 1);
    setLocalError("");
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!IMAGE_EXT.includes(ext)) {
      setLocalError(`Only ${IMAGE_EXT.join(", ")} image files are allowed!`);
      return;
    }
    if (f.size > IMAGE_MAX_MB * 1024 * 1024) {
      setLocalError(`File size should not exceed the maximum limit of ${IMAGE_MAX_MB} MB.`);
      return;
    }

    let dims;
    try {
      dims = await readImageDimensions(f);
    } catch {
      setLocalError("Could not read the image. Please try a different file.");
      return;
    }
    if (dims.width !== IMAGE_REQUIRED_SIZE || dims.height !== IMAGE_REQUIRED_SIZE) {
      URL.revokeObjectURL(dims.url);
      setLocalError(
        `Image must be exactly ${IMAGE_REQUIRED_SIZE} x ${IMAGE_REQUIRED_SIZE} px (uploaded: ${dims.width} x ${dims.height}).`
      );
      return;
    }

    setFile(f);
    setFilePreview(dims.url);
    setRemoveHint(false);
    setLocalError("");
  };

  const clearHint = () => {
    setFile(null);
    setFilePreview("");
    setRemoveHint(true);
  };

  const existingHintUrl = initial?.question_hint ? getDvSourceImageUrl(initial.question_hint) : "";
  const showExistingHint = isEdit && existingHintUrl && !removeHint && !filePreview;

  const finalize = async () => {
    setLocalError("");

    const question_name = draft.question_name.trim();
    if (!question_name) {
      setLocalError("The question field cannot be empty.");
      return;
    }

    const visibleOptions = OPTION_KEYS.slice(0, draft.showOption).map((k) => draft[k].trim());
    if (visibleOptions.some((v) => !v)) {
      setLocalError("All visible options must be filled.");
      return;
    }

    if (!correctIndex || correctIndex > draft.showOption) {
      setLocalError("Please select a valid correct answer!");
      return;
    }

    try {
      await onSubmit({
        question: {
          question_name,
          option_one: draft.option_one.trim(),
          option_two: draft.option_two.trim(),
          option_three: draft.option_three.trim(),
          option_four: draft.option_four.trim(),
          option_five: draft.option_five.trim(),
          option_six: draft.option_six.trim(),
          correct_answer: visibleOptions[correctIndex - 1],
          showOption: draft.showOption,
        },
        file,
        removeHint,
        keepExistingImage: !file && !removeHint,
      });
    } catch {
      /* parent surfaces error */
    }
  };

  return (
    <div className="quiz-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="quiz-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quiz-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="quiz-modal-header">
          <h2 id="quiz-modal-title">{isEdit ? "Edit Question" : "Add Question"}</h2>
          <button type="button" className="quiz-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="quiz-modal-body">
          {localError ? <p className="cr-alert cr-alert-error quiz-modal-error">{localError}</p> : null}

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-question">Question</label>
            <textarea
              id="quiz-modal-question"
              className="quiz-modal-textarea"
              rows={4}
              value={draft.question_name}
              maxLength={QUESTION_MAX}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, question_name: e.target.value.slice(0, QUESTION_MAX) }));
                setLocalError("");
              }}
              placeholder="Question"
              disabled={isSaving}
            />
            <p className="quiz-modal-limit">Max length {QUESTION_MAX} characters</p>
          </div>

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-option-count">Options</label>
            <select
              id="quiz-modal-option-count"
              className="quiz-modal-option-count"
              value={draft.showOption}
              onChange={(e) => setOptionCount(e.target.value)}
              disabled={isSaving}
            >
              {[2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n} options
                </option>
              ))}
            </select>
            <p className="quiz-modal-option-count-hint themeupdate-muted">
              Click an option below to mark it as the correct answer.
            </p>
          </div>

          <div className={`quiz-modal-options-grid quiz-modal-options-grid--${draft.showOption}`}>
            {OPTION_KEYS.slice(0, draft.showOption).map((key, i) => (
              <div className="quiz-modal-field" key={key}>
                <label htmlFor={`quiz-modal-${key}`}>{OPTION_LABELS[i]}</label>
                <input
                  id={`quiz-modal-${key}`}
                  type="text"
                  className={`quiz-option-selectable${correctIndex === i + 1 ? " quiz-option-input-selected" : ""}`}
                  value={draft[key]}
                  maxLength={OPTION_MAX}
                  placeholder={OPTION_LABELS[i]}
                  onChange={(e) => setOption(i, e.target.value)}
                  onClick={() => draft[key].trim() && selectCorrect(i)}
                  disabled={isSaving}
                />
              </div>
            ))}
          </div>

          {!correctIndex ? (
            <p className="quiz-modal-select-hint">
              Fill in the options, then click the correct one to select it.
            </p>
          ) : null}

          <div className="quiz-modal-field quiz-modal-field--full">
            <label htmlFor="quiz-modal-hint-file">Upload Hint (optional)</label>
            <input
              id="quiz-modal-hint-file"
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={onFileChange}
              disabled={isSaving}
            />
            <p className="quiz-modal-limit" style={{ textAlign: "left" }}>
              Allowed formats: JPG, JPEG, PNG. Max size {IMAGE_MAX_MB} MB. Image must be{" "}
              {IMAGE_REQUIRED_SIZE} x {IMAGE_REQUIRED_SIZE} px.
            </p>

            {filePreview ? (
              <img src={filePreview} alt="" className="quiz-modal-image-preview" />
            ) : showExistingHint ? (
              <>
                <img src={existingHintUrl} alt="" className="quiz-modal-image-preview" />
                <button
                  type="button"
                  className="quiz-modal-btn quiz-modal-btn-close remove-hint"
                  onClick={clearHint}
                  disabled={isSaving}
                >
                  Remove
                </button>
              </>
            ) : null}
          </div>
        </div>

        <div className="quiz-modal-footer">
          <button
            type="button"
            className="quiz-modal-btn quiz-modal-btn-save"
            onClick={finalize}
            disabled={isSaving}
          >
            <i className="fa-solid fa-cloud-arrow-up" aria-hidden />
            {isSaving ? "Saving…" : isEdit ? "Update" : "Add New Question"}
          </button>
          <button type="button" className="quiz-modal-btn quiz-modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
